/**
 * Interface representing the result of a password validation.
 */
export interface IPasswordValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Validates a password against the application's strict security policy.
 * The policy requires:
 * - Length between 14 and 50 characters (inclusive)
 * - Contains at least one uppercase letter
 * - Contains at least one lowercase letter
 * - Contains at least one numeric digit
 * - Contains at least one special (non-alphanumeric) character.
 * @param {string} password - The password string to validate.
 * @returns {IPasswordValidationResult} A validation result containing the isValid status and any errorMessage.
 */
export function validatePasswordPolicy(password: string): IPasswordValidationResult {
  if (!password) {
    return { isValid: false, errorMessage: "Password is required." };
  }
  if (password.length < 6) {
    return { isValid: false, errorMessage: "Password must be at least 6 characters long." };
  }
  if (password.length > 50) {
    return { isValid: false, errorMessage: "Password must not exceed 50 characters." };
  }
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, errorMessage: "Password must contain at least one uppercase letter." };
  }
  if (!/[a-z]/.test(password)) {
    return { isValid: false, errorMessage: "Password must contain at least one lowercase letter." };
  }
  if (!/[0-9]/.test(password)) {
    return { isValid: false, errorMessage: "Password must contain at least one number." };
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    return { isValid: false, errorMessage: "Password must contain at least one special character." };
  }
  return { isValid: true };
}
